/* advanced-filters.js — CF Worker filter sidebar + results (movemore-filters).
   Behavior: the collection renders normally (native grid/pagination) until the user touches a
   filter or the sort dropdown, or the page loads with filter state already in the URL — at that
   point we switch permanently to "own" results: /match decides which products match, sorts them,
   and returns just the requested PAGE (real page-numbered pagination, matching the theme's native
   collection.pagination look — see renderPagination() — not the old client-side "load more"
   append). We fetch+render each matched product's real card HTML on demand via Shopify's dynamic
   section rendering endpoint (same trick as buy-assistant.js's assistant-card-renderer).
   Filter + sort state round-trips through the URL (?af=<json>), page through its own ?page=N
   (matching Shopify's native pagination convention) — both so links are shareable/bookmarkable
   and a pasted URL re-applies its state on load. */
(function () {
  const API_BASE = 'https://movemore-filters.konto-agencyjne.workers.dev';
  const SECTION_RENDERER = 'advanced-filters-card-renderer';
  const URL_PARAM = 'af';
  const PAGE_PARAM = 'page';
  const cardCache = {};
  let uid = 0;

  /* ── SIDEBAR RENDERING ── */
  function renderOption(name, value, label) {
    const id = `af-opt-${uid++}`;
    return `<div class="checkbox-container">
      <input type="checkbox" class="checkbox" id="${id}" name="${name}" value="${value}">
      <label for="${id}">${label}</label>
    </div>`;
  }

  function renderFilter(filter, openByDefault) {
    // Boolean filters (only "availability" today) are a single flat switch row, not an
    // accordion — there's no sub-content to collapse, so the details/summary wrapper used for
    // every other filter type below would just add a pointless extra click.
    if (filter.type === 'boolean') {
      const id = `af-opt-${uid++}`;
      const label = filter.filter_key === 'availability' ? 'Tylko na stanie' : filter.display_name;
      return `<div class="af-group accordion" data-filter-key="${filter.filter_key}" data-filter-type="boolean">
        <div class="accordion__toggle">
          <label for="${id}" class="bold">${label}</label>
          <input id="${id}" type="checkbox" class="switch" name="${filter.filter_key}" value="true">
        </div>
      </div>`;
    }

    let body = '';

    if (filter.type === 'list' && filter.values && filter.values.length) {
      body = `<fieldset class="fieldset">${filter.values.map(v => renderOption(filter.filter_key, v.raw_value, v.display_value)).join('')}</fieldset>`;
    } else if (filter.type === 'range' && filter.buckets && filter.buckets.length) {
      // Bucket labels are already fully formatted by the curator (e.g. "Do 7 dni", "2-4 GB") —
      // unlike free min/max range inputs, they must not get filter.unit appended again.
      body = `<fieldset class="fieldset">${filter.buckets.map(b => renderOption(filter.filter_key, b.label, b.label)).join('')}</fieldset>`;
    } else if (filter.type === 'range' && typeof filter.min === 'number' && typeof filter.max === 'number') {
      // Universal price filter — dual slider + linked "od/do" number inputs, backed by real
      // collection price bounds from the backend (see /filters/:handle). Track fill (--range-min/
      // --range-max) and slider<->input sync are wired in wirePriceRange() after render.
      const step = Math.max(1, Math.round((filter.max - filter.min) / 100));
      body = `<div class="af-price-range" data-min="${filter.min}" data-max="${filter.max}">
        <div class="af-price-range-track" style="--range-min: 0%; --range-max: 100%;">
          <input type="range" class="af-price-slider" data-role="slider-min" min="${filter.min}" max="${filter.max}" step="${step}" value="${filter.min}" aria-label="Od kwoty">
          <input type="range" class="af-price-slider" data-role="slider-max" min="${filter.min}" max="${filter.max}" step="${step}" value="${filter.max}" aria-label="Do kwoty">
        </div>
        <div class="af-price-inputs">
          <label class="af-price-input-wrap"><span class="af-price-prefix">zł</span>
            <input type="number" class="field" data-role="min" min="${filter.min}" max="${filter.max}" step="${step}" value="${filter.min}">
          </label>
          <span class="af-price-sep">do</span>
          <label class="af-price-input-wrap"><span class="af-price-prefix">zł</span>
            <input type="number" class="field" data-role="max" min="${filter.min}" max="${filter.max}" step="${step}" value="${filter.max}">
          </label>
        </div>
      </div>`;
    } else if (filter.type === 'range') {
      body = `<div class="af-range">
        <input type="number" class="af-range-input" data-role="min" placeholder="od">
        <input type="number" class="af-range-input" data-role="max" placeholder="do">
      </div>`;
    } else {
      return '';
    }

    // Each filter is its own accordion (name always visible, values collapsed unless opened) —
    // with 17 filters and some carrying 15-20+ options, a flat expanded list ran into the
    // thousands of pixels tall. The universal filters + first 4 advanced ones stay pre-expanded
    // (quick access to the most decision-relevant filters); everything else starts collapsed.
    // Reuses the theme's own accordion CSS classes (accordion, accordion__toggle, circle-chevron,
    // accordion__content) for a consistent look, but WITHOUT is="accordion-disclosure" — that
    // customized-built-in's JS (source not visible to us) caused a ~1s lag + visible jump before
    // content appeared on open. Plain native <details>/<summary> shows/hides accordion__content
    // correctly on its own (that's just the HTML spec, no JS or CSS needed) — no animation, but
    // no lag/jump bug either, which matters more for a working filter than a smooth transition.
    return `<details class="af-group accordion group" data-filter-key="${filter.filter_key}" data-filter-type="${filter.type}"${openByDefault ? ' open' : ''}>
      <summary>
        <div class="accordion__toggle bold">
          <span>${filter.display_name}</span>
          <span class="circle-chevron group-hover:colors group-expanded:colors group-expanded:rotate">
            <svg role="presentation" focusable="false" width="8" height="6" class="icon icon-chevron-bottom-small" viewBox="0 0 8 6">
              <path d="m1 1.5 3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.5"></path>
            </svg>
          </span>
        </div>
      </summary>
      <div class="accordion__content">${body}</div>
    </details>`;
  }

  function renderSidebar(filters) {
    const universal = filters.filter(f => f.is_universal);
    const advanced = filters.filter(f => !f.is_universal);
    const resetBtnHtml = '<button type="button" class="af-reset button" data-af-reset>Wyczyść filtry</button>';

    // Below 1000px .collection__facets is forced visible (no side-by-side grid, no room for a
    // "few pre-opened + rest behind a popup" split) — one flat list, every filter collapsed, so
    // the initial view stays compact regardless of how many filters/options there are.
    if (!window.matchMedia('(min-width: 1000px)').matches) {
      return universal.concat(advanced).map(f => renderFilter(f, false)).join('') + resetBtnHtml;
    }

    const visible = advanced.slice(0, 4);
    const rest = advanced.slice(4);

    let html = universal.map(f => renderFilter(f, true)).join('')
      + visible.map(f => renderFilter(f, true)).join('');

    if (rest.length) {
      html += `<button type="button" class="af-more-toggle" data-af-more-open>Pokaż więcej filtrów (${rest.length})</button>
      <dialog class="af-more-dialog" data-af-more-dialog>
        <div class="af-more-dialog-header">
          <p class="af-more-dialog-title">Więcej filtrów</p>
          <button type="button" class="af-more-dialog-close" data-af-more-close aria-label="Zamknij">&times;</button>
        </div>
        <div class="af-more-content">${rest.map(f => renderFilter(f, false)).join('')}</div>
        <button type="button" class="af-more-apply button" data-af-more-close>Pokaż wyniki</button>
      </dialog>`;
    }

    return html + resetBtnHtml;
  }

  /* ── READING SELECTION FROM THE DOM (single source of truth, no parallel JS state) ── */
  function readGroupSelection(group) {
    if (group.dataset.filterType === 'boolean') {
      const cb = group.querySelector('input[type="checkbox"]');
      return cb && cb.checked ? { value: true } : {};
    }

    const numberInputs = group.querySelectorAll('input[type="number"]');
    if (numberInputs.length) {
      const min = group.querySelector('[data-role="min"]');
      const max = group.querySelector('[data-role="max"]');
      const result = {};
      if (min && min.value !== '') result.min = Number(min.value);
      if (max && max.value !== '') result.max = Number(max.value);
      return result;
    }

    const checked = Array.from(group.querySelectorAll('input[type="checkbox"]:checked')).map(i => i.value);
    return group.dataset.filterType === 'range' ? { buckets: checked } : { values: checked };
  }

  function collectFiltersFromDOM(sidebar) {
    const filters = {};
    sidebar.querySelectorAll('.af-group').forEach((group) => {
      const sel = readGroupSelection(group);
      const hasValue = (sel.values && sel.values.length) || (sel.buckets && sel.buckets.length) || sel.min !== undefined || sel.max !== undefined || sel.value;
      if (hasValue) filters[group.dataset.filterKey] = sel;
    });
    return filters;
  }

  /* ── URL STATE ── */
  function readUrlState() {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get(PAGE_PARAM) ?? '', 10);
    const raw = params.get(URL_PARAM);
    let state = null;
    if (raw) {
      try {
        state = JSON.parse(raw);
      } catch (e) {
        state = null;
      }
    }
    if (!state && !(page > 0)) return null;
    return Object.assign({ filters: {}, sort: undefined }, state, { page: page > 0 ? page : 1 });
  }

  function writeUrlState(filters, sort, page) {
    const params = new URLSearchParams(window.location.search);
    if (Object.keys(filters).length || sort) {
      params.set(URL_PARAM, JSON.stringify({ filters, sort: sort || undefined }));
    } else {
      params.delete(URL_PARAM);
    }
    // Own page param, matching Shopify's native ?page=N pagination convention (kept separate
    // from the af=<json> blob so it reads the same way a native paginated URL would).
    if (page && page > 1) {
      params.set(PAGE_PARAM, String(page));
    } else {
      params.delete(PAGE_PARAM);
    }
    const query = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
  }

  function applyStateToSidebar(sidebar, state) {
    if (!state || !state.filters) return;
    Object.entries(state.filters).forEach(([key, sel]) => {
      const group = sidebar.querySelector(`.af-group[data-filter-key="${key}"]`);
      if (!group) return;

      (sel.values || sel.buckets || []).forEach((value) => {
        const input = group.querySelector(`input[value="${CSS.escape(value)}"]`);
        if (input) input.checked = true;
      });

      if (sel.min !== undefined || sel.max !== undefined) {
        const min = group.querySelector('[data-role="min"]');
        const max = group.querySelector('[data-role="max"]');
        if (min && sel.min !== undefined) min.value = sel.min;
        if (max && sel.max !== undefined) max.value = sel.max;
        // Price range also has slider thumbs mirroring the number inputs — wirePriceRange()'s
        // first updateTrack() call (called after this, in initAdvancedFilters) picks these up.
        const sliderMin = group.querySelector('[data-role="slider-min"]');
        const sliderMax = group.querySelector('[data-role="slider-max"]');
        if (sliderMin && sel.min !== undefined) sliderMin.value = sel.min;
        if (sliderMax && sel.max !== undefined) sliderMax.value = sel.max;
      }

      if (sel.value) {
        const input = group.querySelector('input[type="checkbox"]');
        if (input) input.checked = true;
      }
    });
  }

  /* Dual-thumb price slider <-> "od/do" number inputs, two-way sync + visual fill track. Plain
     DOM wiring, not a reused theme custom element — we don't control/see that component's JS, so
     safer to own this small piece than guess at compatibility. */
  function wirePriceRange(root) {
    root.querySelectorAll('.af-price-range').forEach((wrap) => {
      const min = Number(wrap.dataset.min);
      const max = Number(wrap.dataset.max);
      const track = wrap.querySelector('.af-price-range-track');
      const sliderMin = wrap.querySelector('[data-role="slider-min"]');
      const sliderMax = wrap.querySelector('[data-role="slider-max"]');
      const inputMin = wrap.querySelector('input[type="number"][data-role="min"]');
      const inputMax = wrap.querySelector('input[type="number"][data-role="max"]');
      if (!sliderMin || !sliderMax || !inputMin || !inputMax) return;

      function updateTrack() {
        const lo = Number(sliderMin.value);
        const hi = Number(sliderMax.value);
        track.style.setProperty('--range-min', `${((lo - min) / (max - min)) * 100}%`);
        track.style.setProperty('--range-max', `${((hi - min) / (max - min)) * 100}%`);
      }

      sliderMin.addEventListener('input', () => {
        if (Number(sliderMin.value) > Number(sliderMax.value)) sliderMin.value = sliderMax.value;
        inputMin.value = sliderMin.value;
        updateTrack();
      });
      sliderMax.addEventListener('input', () => {
        if (Number(sliderMax.value) < Number(sliderMin.value)) sliderMax.value = sliderMin.value;
        inputMax.value = sliderMax.value;
        updateTrack();
      });
      inputMin.addEventListener('change', () => {
        const v = Number(inputMin.value);
        if (!Number.isNaN(v)) sliderMin.value = Math.min(Math.max(v, min), Number(sliderMax.value));
        updateTrack();
      });
      inputMax.addEventListener('change', () => {
        const v = Number(inputMax.value);
        if (!Number.isNaN(v)) sliderMax.value = Math.max(Math.min(v, max), Number(sliderMin.value));
        updateTrack();
      });

      updateTrack();
    });
  }

  /* ── API ── */
  async function fetchFilterConfig(handle) {
    const res = await fetch(`${API_BASE}/filters/${handle}`);
    if (!res.ok) throw new Error(res.status);
    return res.json();
  }

  async function fetchMatch(handle, filters, sort, page) {
    const res = await fetch(`${API_BASE}/filters/${handle}/match`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filters, sort: sort || undefined, page })
    });
    if (!res.ok) throw new Error(res.status);
    return res.json();
  }

  async function fetchCardHTML(handle) {
    if (cardCache[handle]) return cardCache[handle];
    try {
      const res = await fetch(`/products/${handle}?sections=${SECTION_RENDERER}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      const html = data[SECTION_RENDERER] || '';
      cardCache[handle] = html;
      return html;
    } catch (e) {
      console.warn('[advanced-filters] karta produktu nie wczytana:', handle, e);
      return null;
    }
  }

  /* ── RESULTS RENDERING (own mode) ── */
  function productsCountLabel(n) {
    if (n === 1) return `${n} produkt`;
    const mod10 = n % 10, mod100 = n % 100;
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return `${n} produkty`;
    return `${n} produktów`;
  }

  function updateCount(root, count) {
    const el = root.querySelector('[data-af-count]');
    if (el) el.textContent = `Znaleziono ${productsCountLabel(count)}`;
  }

  function toggleEmpty(root, isEmpty) {
    const empty = root.querySelector('[data-af-empty]');
    const grid = root.querySelector('[data-af-grid]');
    if (empty) empty.hidden = !isEmpty;
    if (grid) grid.hidden = isEmpty;
  }

  function switchToOwnMode(root) {
    if (root.dataset.afMode === 'own') return;
    root.dataset.afMode = 'own';
    const pagination = root.parentElement && root.parentElement.querySelector('[data-af-native-pagination]');
    if (pagination) pagination.hidden = true;
  }

  /* Real page-numbered pagination (matches the theme's native collection.pagination look) — the
     backend already returns just the requested page's products, so this only ever renders one
     page's worth of cards, no incremental "load more" state to track. */
  async function renderCards(root, products) {
    const grid = root.querySelector('[data-af-grid]');
    if (!grid) return;
    grid.innerHTML = '';
    if (!products.length) return;

    const htmls = await Promise.all(products.map(p => fetchCardHTML(p.handle)));
    htmls.forEach((html, i) => {
      if (!html) return;
      const wrap = document.createElement('div');
      wrap.className = 'af-item';
      wrap.dataset.productId = products[i].id;
      wrap.innerHTML = html;
      grid.appendChild(wrap);
    });
  }

  function renderPagination(root, page, totalPages) {
    const wrap = root.querySelector('[data-af-pagination]');
    if (!wrap) return;

    if (totalPages <= 1) {
      wrap.hidden = true;
      wrap.innerHTML = '';
      return;
    }
    wrap.hidden = false;

    const prev = page > 1
      ? `<a class="pagination__item group" rel="prev" aria-label="Przejdź do strony ${page - 1}" href="?${PAGE_PARAM}=${page - 1}" data-af-page="${page - 1}">
          <span class="animated-arrow animated-arrow--reverse"></span>
        </a>`
      : `<span class="pagination__item pagination__item--disabled">
          <span class="animated-arrow animated-arrow--reverse"></span>
        </span>`;

    const next = page < totalPages
      ? `<a class="pagination__item group" rel="next" aria-label="Przejdź do strony ${page + 1}" href="?${PAGE_PARAM}=${page + 1}" data-af-page="${page + 1}">
          <span class="animated-arrow"></span>
        </a>`
      : `<span class="pagination__item pagination__item--disabled">
          <span class="animated-arrow"></span>
        </span>`;

    wrap.innerHTML = `<nav class="collection__pagination pagination" role="navigation" aria-label="Nawigacja stronicowania">
      ${prev}
      <span class="pagination__current">${page}&nbsp;&nbsp;/&nbsp;&nbsp;${totalPages}</span>
      ${next}
    </nav>`;

    wrap.querySelectorAll('[data-af-page]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        updateResults(root, { page: Number(link.dataset.afPage) });
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  async function updateResults(root, opts) {
    const sidebar = root.querySelector('[data-af-sidebar]');
    const sortSelect = root.querySelector('[data-af-sort]');
    const filters = collectFiltersFromDOM(sidebar);
    const sort = sortSelect ? sortSelect.value : '';
    // A page number in opts means "user clicked a pagination link" — keep it. Any other trigger
    // (filter/sort change, reset) is a new result set, so it always starts back at page 1.
    const requestedPage = (opts && opts.page) || 1;

    writeUrlState(filters, sort, requestedPage);

    if (!Object.keys(filters).length && !sort && root.dataset.afMode !== 'own') {
      return; /* nothing selected yet — stay on the native grid */
    }

    switchToOwnMode(root);

    let data;
    try {
      data = await fetchMatch(root.dataset.collectionHandle, filters, sort, requestedPage);
    } catch (e) {
      console.warn('[advanced-filters] zapytanie /match nie powiodło się:', e);
      return;
    }

    updateCount(root, data.matched_count);
    toggleEmpty(root, data.matched_count === 0);
    await renderCards(root, data.products || []);
    renderPagination(root, data.page || 1, data.total_pages || 1);
  }

  /* ── INIT ── */
  async function initAdvancedFilters(root) {
    if (root.dataset.afInitialized) return;
    root.dataset.afInitialized = 'true';

    const handle = root.dataset.collectionHandle;
    const sidebar = root.querySelector('[data-af-sidebar]');
    if (!handle || !sidebar) return;
    const sidebarHomeParent = sidebar.parentElement;

    try {
      const data = await fetchFilterConfig(handle);
      if (!data.filterable || !data.filters || !data.filters.length) {
        sidebar.innerHTML = '<p class="af-empty-message">Ta kolekcja nie ma jeszcze skonfigurowanych filtrów.</p>';
        return;
      }
      sidebar.innerHTML = renderSidebar(data.filters);
    } catch (e) {
      console.warn('[advanced-filters] nie udało się wczytać configu filtrów:', e);
      sidebar.innerHTML = '<p class="af-empty-message">Nie udało się wczytać filtrów.</p>';
      return;
    }

    const state = readUrlState();
    const sortSelect = root.querySelector('[data-af-sort]');
    applyStateToSidebar(sidebar, state);
    wirePriceRange(sidebar);
    if (state && state.sort && sortSelect) sortSelect.value = state.sort;

    sidebar.addEventListener('change', (e) => {
      // Inside a popup (desktop "more filters" or mobile), changes just accumulate silently —
      // /match only runs once on close (button, backdrop, or Escape — dialog's native 'close'
      // event covers all three), not per-checkbox, so results don't churn while still choosing.
      if (e.target.closest('.af-group') && !e.target.closest('dialog')) updateResults(root);
    });

    const moreDialog = sidebar.querySelector('[data-af-more-dialog]');
    const moreOpenBtn = sidebar.querySelector('[data-af-more-open]');
    if (moreDialog && moreOpenBtn) {
      moreOpenBtn.addEventListener('click', () => moreDialog.showModal());
      moreDialog.querySelectorAll('[data-af-more-close]').forEach((btn) => {
        btn.addEventListener('click', () => moreDialog.close());
      });
      moreDialog.addEventListener('click', (e) => {
        if (e.target === moreDialog) moreDialog.close(); // click on backdrop
      });
      moreDialog.addEventListener('close', () => updateResults(root));
    }

    // Mobile: renderSidebar() already produced a flat, fully-collapsed list (no nested "more"
    // dialog — moreDialog/moreOpenBtn above are simply absent from the DOM in that branch), so
    // this is the only popup on mobile, not two stacked ones. Moves the real sidebar node into
    // the dialog on open and back to its normal spot on close — one live copy, no state to sync.
    const mobileDialog = root.querySelector('[data-af-mobile-dialog]');
    const mobileBody = root.querySelector('[data-af-mobile-body]');
    const mobileOpenBtn = root.querySelector('[data-af-mobile-open]');
    if (mobileDialog && mobileBody && mobileOpenBtn) {
      mobileOpenBtn.addEventListener('click', () => {
        mobileBody.appendChild(sidebar);
        mobileDialog.showModal();
      });
      mobileDialog.querySelectorAll('[data-af-mobile-close]').forEach((btn) => {
        btn.addEventListener('click', () => mobileDialog.close());
      });
      mobileDialog.addEventListener('click', (e) => {
        if (e.target === mobileDialog) mobileDialog.close(); // click on backdrop
      });
      mobileDialog.addEventListener('close', () => {
        sidebarHomeParent.appendChild(sidebar); // always restore, incl. Esc-key dismissal
        updateResults(root);
      });
    }

    const resetBtn = sidebar.querySelector('[data-af-reset]');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        sidebar.querySelectorAll('input[type="checkbox"]').forEach((i) => { i.checked = false; });
        sidebar.querySelectorAll('input[type="number"]').forEach((i) => { i.value = ''; });
        sidebar.querySelectorAll('.af-price-range').forEach((wrap) => {
          const min = Number(wrap.dataset.min);
          const max = Number(wrap.dataset.max);
          const track = wrap.querySelector('.af-price-range-track');
          const sliderMin = wrap.querySelector('[data-role="slider-min"]');
          const sliderMax = wrap.querySelector('[data-role="slider-max"]');
          if (!sliderMin || !sliderMax) return;
          sliderMin.value = min;
          sliderMax.value = max;
          track.style.setProperty('--range-min', '0%');
          track.style.setProperty('--range-max', '100%');
        });
        updateResults(root);
      });
    }

    if (sortSelect) sortSelect.addEventListener('change', () => updateResults(root));

    // Sort dropdown, styled like the theme's native facet-sort-by popover — but plain markup +
    // our own JS (see the comment in the liquid template for why). The hidden <select> above
    // stays the single source of truth; this just proxies clicks into it.
    const sortToggle = root.querySelector('[data-af-sort-toggle]');
    const sortPopover = root.querySelector('[data-af-sort-popover]');
    const sortLabel = root.querySelector('[data-af-sort-label]');
    if (sortToggle && sortPopover && sortLabel && sortSelect) {
      const syncLabel = () => {
        const opt = sortPopover.querySelector(`[data-value="${CSS.escape(sortSelect.value)}"]`);
        sortLabel.textContent = opt ? opt.textContent.trim() : 'Domyślnie';
      };
      syncLabel();

      // Toggling the `hidden` attribute didn't work: the theme's own .popover class apparently
      // sets its own `display` unconditionally, and any author-origin rule beats the UA-origin
      // [hidden]{display:none} default regardless of specificity. A class we fully control (with
      // higher specificity via .sort-by-facet .af-sort-popover.is-open in the CSS) sidesteps that.
      const closePopover = () => {
        sortPopover.classList.remove('is-open');
        sortToggle.setAttribute('aria-expanded', 'false');
      };

      sortToggle.addEventListener('click', () => {
        const willOpen = !sortPopover.classList.contains('is-open');
        sortPopover.classList.toggle('is-open', willOpen);
        sortToggle.setAttribute('aria-expanded', String(willOpen));
      });

      sortPopover.querySelectorAll('[data-value]').forEach((opt) => {
        opt.addEventListener('click', () => {
          sortSelect.value = opt.dataset.value;
          syncLabel();
          closePopover();
          sortSelect.dispatchEvent(new Event('change'));
        });
      });

      document.addEventListener('click', (e) => {
        if (sortPopover.classList.contains('is-open') && !e.target.closest('[data-af-sort-wrap]')) closePopover();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && sortPopover.classList.contains('is-open')) closePopover();
      });
    }

    const resetEmptyBtn = root.querySelector('[data-af-reset-empty]');
    if (resetEmptyBtn && resetBtn) resetEmptyBtn.addEventListener('click', () => resetBtn.click());

    if (state && ((state.filters && Object.keys(state.filters).length) || state.sort)) {
      await updateResults(root, { page: state.page });
    }
  }

  function init() {
    document.querySelectorAll('.advanced-filters').forEach(initAdvancedFilters);
  }

  init();
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('.advanced-filters').forEach(initAdvancedFilters);
  });
})();
